import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { getSettings, updateSetting, getRateCounters } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Separator } from '@/components/ui/separator'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

// ── General tab ───────────────────────────────────────────────────────────────

const generalSchema = z.object({
  crm_adapter: z.enum(['noop', 'qsecdef']),
  timezone: z.string().min(1),
})
type GeneralForm = z.infer<typeof generalSchema>

function GeneralTab() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['settings'], queryFn: getSettings })

  const settings = (data ?? {}) as Record<string, unknown>

  const { register, handleSubmit, setValue, watch, formState: { errors, isDirty } } =
    useForm<GeneralForm>({
      resolver: zodResolver(generalSchema),
      values: {
        crm_adapter: (settings.crm_adapter as GeneralForm['crm_adapter']) ?? 'noop',
        timezone: (settings.timezone as string) ?? 'Europe/Paris',
      },
    })

  const mutation = useMutation({
    mutationFn: async (form: GeneralForm) => {
      await updateSetting('crm_adapter', form.crm_adapter)
      await updateSetting('timezone', form.timezone)
    },
    onSuccess: () => {
      toast.success('Settings saved.')
      qc.invalidateQueries({ queryKey: ['settings'] })
    },
    onError: () => toast.error('Failed to save settings.'),
  })

  if (isLoading) return <SettingsSkeleton />

  return (
    <form onSubmit={handleSubmit((d) => mutation.mutate(d))} className="space-y-5 max-w-md">
      <div className="space-y-1.5">
        <Label>CRM adapter</Label>
        <Select
          value={watch('crm_adapter')}
          onValueChange={(v) => setValue('crm_adapter', v as GeneralForm['crm_adapter'], { shouldDirty: true })}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="noop">No-op (testing)</SelectItem>
            <SelectItem value="qsecdef">QSECDEF CRM</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-1.5">
        <Label>LLM model</Label>
        <Input value="Claude Haiku 4.5" readOnly className="text-muted-foreground" />
        <p className="text-xs text-muted-foreground">Model is managed centrally and cannot be changed here.</p>
      </div>

      <div className="space-y-1.5">
        <Label>Timezone</Label>
        <Input
          placeholder="Europe/Paris"
          {...register('timezone')}
        />
        {errors.timezone && (
          <p className="text-xs text-destructive">{errors.timezone.message}</p>
        )}
      </div>

      <Button type="submit" disabled={!isDirty || mutation.isPending}>
        {mutation.isPending ? 'Saving…' : 'Save changes'}
      </Button>
    </form>
  )
}

// ── Rate limits tab ───────────────────────────────────────────────────────────

const ACTION_TYPES = [
  'connection_request',
  'message',
  'email',
  'profile_view',
  'scraping',
] as const
type ActionType = (typeof ACTION_TYPES)[number]

const LABELS: Record<ActionType, string> = {
  connection_request: 'Connection requests',
  message: 'Messages',
  email: 'Emails',
  profile_view: 'Profile views',
  scraping: 'Scraping',
}

const rateLimitsSchema = z.object(
  Object.fromEntries(
    ACTION_TYPES.map((t) => [
      t,
      z.object({
        daily_cap: z.coerce.number().int().min(0),
        company_cap: z.coerce.number().int().min(0).optional(),
        ramp_pct: z.coerce.number().min(0).max(100).optional(),
      }),
    ]),
  ) as Record<ActionType, z.ZodObject<{ daily_cap: z.ZodNumber; company_cap: z.ZodOptional<z.ZodNumber>; ramp_pct: z.ZodOptional<z.ZodNumber> }>>,
)
type RateLimitsForm = z.infer<typeof rateLimitsSchema>

function RateLimitsTab() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['settings'], queryFn: getSettings })
  const settings = (data ?? {}) as Record<string, unknown>

  type ActionSettings = { daily_cap: number; company_cap?: number; ramp_pct?: number }
  const rateLimits = (settings.rate_limits ?? {}) as Record<ActionType, ActionSettings>

  const { register, handleSubmit, formState: { errors, isDirty } } = useForm<RateLimitsForm>({
    resolver: zodResolver(rateLimitsSchema),
    values: Object.fromEntries(
      ACTION_TYPES.map((t) => [
        t,
        {
          daily_cap: rateLimits[t]?.daily_cap ?? 0,
          company_cap: rateLimits[t]?.company_cap ?? 0,
          ramp_pct: rateLimits[t]?.ramp_pct ?? 100,
        },
      ]),
    ) as RateLimitsForm,
  })

  const mutation = useMutation({
    mutationFn: (form: RateLimitsForm) => updateSetting('rate_limits', form),
    onSuccess: () => {
      toast.success('Rate limits saved.')
      qc.invalidateQueries({ queryKey: ['settings'] })
    },
    onError: () => toast.error('Failed to save rate limits.'),
  })

  if (isLoading) return <SettingsSkeleton />

  return (
    <form onSubmit={handleSubmit((d) => mutation.mutate(d))} className="space-y-4">
      <div className="rounded-md border overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/30">
              <th className="px-3 py-2 text-left font-medium text-muted-foreground">Action</th>
              <th className="px-3 py-2 text-left font-medium text-muted-foreground">Daily cap</th>
              <th className="px-3 py-2 text-left font-medium text-muted-foreground">Company cap</th>
              <th className="px-3 py-2 text-left font-medium text-muted-foreground">Ramp-up %</th>
            </tr>
          </thead>
          <tbody>
            {ACTION_TYPES.map((t) => (
              <tr key={t} className="border-b last:border-0">
                <td className="px-3 py-2.5 font-medium">{LABELS[t]}</td>
                <td className="px-3 py-2.5">
                  <Input
                    type="number"
                    min={0}
                    className="h-7 w-20 text-xs"
                    {...register(`${t}.daily_cap`)}
                  />
                </td>
                <td className="px-3 py-2.5">
                  <Input
                    type="number"
                    min={0}
                    className="h-7 w-20 text-xs"
                    {...register(`${t}.company_cap`)}
                  />
                </td>
                <td className="px-3 py-2.5">
                  <Input
                    type="number"
                    min={0}
                    max={100}
                    className="h-7 w-20 text-xs"
                    {...register(`${t}.ramp_pct`)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Button type="submit" disabled={!isDirty || mutation.isPending}>
        {mutation.isPending ? 'Saving…' : 'Save rate limits'}
      </Button>
    </form>
  )
}

// ── Ramp-up tab ───────────────────────────────────────────────────────────────

const rampUpSchema = z.object({
  start_date: z.string().min(1),
  total_weeks: z.coerce.number().int().min(1).max(52),
})
type RampUpForm = z.infer<typeof rampUpSchema>

function RampUpTab() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({ queryKey: ['settings'], queryFn: getSettings })
  const settings = (data ?? {}) as Record<string, unknown>
  const rampUp = (settings.ramp_up ?? {}) as {
    start_date?: string
    total_weeks?: number
    current_week?: number
  }

  const { register, handleSubmit, formState: { errors, isDirty } } = useForm<RampUpForm>({
    resolver: zodResolver(rampUpSchema),
    values: {
      start_date: rampUp.start_date ?? '',
      total_weeks: rampUp.total_weeks ?? 8,
    },
  })

  const mutation = useMutation({
    mutationFn: (form: RampUpForm) => updateSetting('ramp_up', form),
    onSuccess: () => {
      toast.success('Ramp-up settings saved.')
      qc.invalidateQueries({ queryKey: ['settings'] })
    },
    onError: () => toast.error('Failed to save ramp-up settings.'),
  })

  const resetMutation = useMutation({
    mutationFn: () => updateSetting('ramp_up_reset', true),
    onSuccess: () => {
      toast.success('Ramp-up reset to week 1.')
      qc.invalidateQueries({ queryKey: ['settings'] })
    },
    onError: () => toast.error('Could not reset ramp-up.'),
  })

  if (isLoading) return <SettingsSkeleton />

  return (
    <form onSubmit={handleSubmit((d) => mutation.mutate(d))} className="space-y-5 max-w-md">
      <div className="rounded-md border p-4 bg-muted/20 space-y-1">
        <p className="text-xs text-muted-foreground">Current week</p>
        <p className="text-2xl font-semibold">
          {rampUp.current_week ?? '—'}
          <span className="text-base font-normal text-muted-foreground">
            {' '}/ {rampUp.total_weeks ?? '—'}
          </span>
        </p>
      </div>

      <div className="space-y-1.5">
        <Label>Ramp-up start date</Label>
        <Input type="date" {...register('start_date')} />
        {errors.start_date && (
          <p className="text-xs text-destructive">{errors.start_date.message}</p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label>Total weeks</Label>
        <Input type="number" min={1} max={52} {...register('total_weeks')} />
        {errors.total_weeks && (
          <p className="text-xs text-destructive">{errors.total_weeks.message}</p>
        )}
      </div>

      <div className="flex items-center gap-3">
        <Button type="submit" disabled={!isDirty || mutation.isPending}>
          {mutation.isPending ? 'Saving…' : 'Save'}
        </Button>

        <Separator orientation="vertical" className="h-6" />

        <Button
          type="button"
          variant="outline"
          onClick={() => resetMutation.mutate()}
          disabled={resetMutation.isPending}
        >
          Force reset to week 1
        </Button>
      </div>
    </form>
  )
}

function SettingsSkeleton() {
  return (
    <div className="space-y-3 max-w-md">
      {Array.from({ length: 4 }).map((_, i) => (
        <Skeleton key={i} className="h-9 w-full" />
      ))}
    </div>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function Settings() {
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-xl font-semibold">Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">
          System configuration, rate limits, and ramp-up schedule.
        </p>
      </div>

      <Tabs defaultValue="general">
        <TabsList>
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="rate-limits">Rate limits</TabsTrigger>
          <TabsTrigger value="ramp-up">Ramp-up</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="pt-5">
          <GeneralTab />
        </TabsContent>

        <TabsContent value="rate-limits" className="pt-5">
          <RateLimitsTab />
        </TabsContent>

        <TabsContent value="ramp-up" className="pt-5">
          <RampUpTab />
        </TabsContent>
      </Tabs>
    </div>
  )
}
